import CachedIcon from '@mui/icons-material/Cached';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { DataAPIContext, UPDATE } from '../../api';
import FormActionButtons from '../../components/FormActionButtons';
import RemoteAutocomplete from '../../components/RemoteAutocomplete';

const formatCurrentOwner = (user) => {
    if (!user) {
        return '(no owner)';
    }
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    if (name && user.email) {
        return `${name} <${user.email}>`;
    }
    return name || user.email || `User #${user.id}`;
};

const ChangeBookingOwnerDialog = ({ open, onClose, tripUser, onChanged }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [target, setTarget] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            setTarget(null);
            setSubmitting(false);
        }
    }, [open]);

    const canSubmit = target && target.id && target.id !== tripUser?.user?.id && !submitting;

    const handleConfirm = async () => {
        if (!canSubmit) {
            return;
        }
        setSubmitting(true);
        try {
            await dataAPI(UPDATE, 'tour_user', {
                id: tripUser.id,
                data: { target_user_id: target.id },
                urlSuffix: 'switch_user/',
            });
            enqueueSnackbar('Booking owner changed', { variant: 'success' });
            onChanged && onChanged({ tripUserId: tripUser.id, targetUser: target });
            onClose();
        } catch (err) {
            const msg = err?.body?.error || err?.message || 'Failed to change booking owner';
            enqueueSnackbar(msg, { variant: 'error' });
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Change booking owner</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Move this booking to a different user. Chat membership and the per-user manifest
                    will be reconciled for both users. This action is logged.
                </DialogContentText>
                <Stack spacing={2}>
                    <div>
                        <Typography variant="caption" color="text.secondary">Current owner</Typography>
                        <Typography variant="body2">{formatCurrentOwner(tripUser?.user)}</Typography>
                    </div>
                    <RemoteAutocomplete
                        resource="user"
                        urlSuffix={null}
                        label="New owner"
                        variant="filled"
                        size="small"
                        getOptionLabel={(opt) => {
                            if (!opt) return '';
                            const name = [opt.first_name, opt.last_name].filter(Boolean).join(' ').trim();
                            return name && opt.email ? `${name} <${opt.email}>` : (name || opt.email || `User #${opt.id}`);
                        }}
                        value={target}
                        onChange={(_ev, value) => setTarget(value)}
                        disabled={submitting}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <FormActionButtons
                    size="medium"
                    onCancel={onClose}
                    cancelDisabled={submitting}
                    onSubmit={handleConfirm}
                    submitDisabled={!canSubmit}
                    submitLabel="Change owner"
                    submitIcon={<CachedIcon />}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ChangeBookingOwnerDialog;
